import { ADMIN_REGISTRY, AdminGroupDefinition } from './admin-registry';
import { AppMenuItem } from './main-menu.model';

/**
 * Sidebar menu model for the Administration area (`/admin/*`). Pure: no DI,
 * no router, so it can be specced and storied without the store.
 *
 * Derived from {@link ADMIN_REGISTRY} the way `buildMainMenuModel` is derived
 * from `APP_REGISTRY` (SCRUM-622, admin masthead epic story 4): an
 * "Administration" header with one row per group (icon + label, no
 * sub-items; the group's pages are the admin masthead tabs) and an "Apps"
 * header with a single "Back to My Workspace" link out of the admin area,
 * visible to everyone who can reach it (SCRUM-605). The page-by-page tree
 * this replaced listed every page and the "Create" actions as sub-items; the
 * create actions are now buttons on the list pages.
 *
 * A row links to the group's `home` and carries the group's `activeWhen`, so
 * it stays lit on every page, create and entity URL of the group
 * (`isMenuItemActive`). Gates are copied from the group so `filterMenuTree`
 * and the masthead agree on who sees what.
 *
 * `AppMenu.updateMenuVisibility()` deep-clones the model through JSON before
 * filtering, so everything here must stay plain data.
 */

/** Where "Back to My Workspace" goes: the My Workspace ("At a Glance") page. */
export const MY_WORKSPACE_PATH = '/my/workspace';

/** The "Apps" header shown under the admin menu: one link back to My Workspace. */
export function buildBackToWorkspaceSection(): AppMenuItem {
  return {
    label: 'Apps',
    icon: 'pi pi-home',
    items: [
      {
        label: 'Back to My Workspace',
        icon: 'pi pi-fw pi-arrow-left',
        routerLink: [MY_WORKSPACE_PATH],
      },
    ],
  };
}

/**
 * Builds the admin sidebar model from the registry, in registry order:
 * "Administration" with one row per group, then "Apps" › Back to My Workspace.
 */
export function buildAdminMenuModel(registry: readonly AdminGroupDefinition[] = ADMIN_REGISTRY): AppMenuItem[] {
  return [
    {
      label: 'Administration',
      icon: 'pi pi-th-large',
      items: registry.map(toAdminMenuItem),
    },
    buildBackToWorkspaceSection(),
  ];
}

function toAdminMenuItem(group: AdminGroupDefinition): AppMenuItem {
  const item: AppMenuItem = {
    label: group.label,
    icon: group.icon,
    routerLink: [group.home],
    activeWhen: [...group.activeWhen],
  };
  // Only copy the gates the group declares, as `toAppMenuItem` does for apps
  // (an undefined key would be dropped by the JSON clone anyway).
  if (group.requiredRoles) {
    item.requiredRoles = [...group.requiredRoles];
  }
  if (group.requireAllRoles !== undefined) {
    item.requireAllRoles = group.requireAllRoles;
  }
  if (group.requiresWmEnabledTeam !== undefined) {
    item.requiresWmEnabledTeam = group.requiresWmEnabledTeam;
  }
  return item;
}
